import { Paperclip, Plus, Trash2 } from "lucide-react";
import { type ChangeEvent, useRef } from "react";
import { toast } from "sonner";
import { useAttachments, useDeleteAttachment, useUploadAttachment } from "../../../hooks/use-files";
import { Button } from "../../ui/button";
import { useConfirm } from "../../ui/confirm-dialog";
import { ErrorState, LoadingSpinner } from "../../ui/loading";
import { Section } from "../view/section";

interface Props {
  recordId: string;
}

export function SectionAttachmentsForm({ recordId }: Props): React.ReactElement {
  const { data, isLoading, isError, error } = useAttachments(recordId);
  const uploadAttachment = useUploadAttachment(recordId);
  const deleteAttachment = useDeleteAttachment(recordId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { confirm, dialog } = useConfirm();

  const attachments = [...(data?.attachments ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);

  const handleFileSelected = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    uploadAttachment.mutate(file, {
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : "Failed to upload attachment");
      },
    });
  };

  const handleDelete = async (attachmentId: string): Promise<void> => {
    const ok = await confirm({
      title: "Delete attachment",
      description: "This removes the attachment from the record.",
      destructive: true,
    });
    if (!ok) return;
    deleteAttachment.mutate(attachmentId, {
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : "Failed to delete attachment");
      },
    });
  };

  return (
    <Section id="attachments" title="Attachments">
      {isLoading && (
        <div className="flex justify-center py-6">
          <LoadingSpinner />
        </div>
      )}
      {isError && <ErrorState message={error instanceof Error ? error.message : "Failed to load attachments"} />}
      {!isLoading && !isError && (
        <>
          {attachments.length > 0 ? (
            <div className="space-y-2 mb-3">
              {attachments.map((att) => (
                <div key={att.id} className="flex items-center justify-between gap-3 rounded-md border p-2.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
                    <a
                      href={att.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-medium truncate text-primary hover:underline"
                      data-testid={`attachment-link-${att.id}`}
                    >
                      {att.filename}
                    </a>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDelete(att.id)}
                    className="text-muted-foreground hover:text-destructive shrink-0"
                    data-testid={`remove-attachment-${att.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground mb-3">No attachments.</p>
          )}
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelected}
            className="hidden"
            data-testid="attachment-file-input"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={uploadAttachment.isPending}
            onClick={() => fileInputRef.current?.click()}
            data-testid="add-attachment"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Attachment
          </Button>
        </>
      )}
      {dialog}
    </Section>
  );
}
